import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService as Auth0, User } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { getUserBySub, getUsersByOrganization } from '../user/store/user.actions';
import { fetchCurrentSponsor } from '../store/actions/ui.actions';
import { PermissionService } from '../permission/permission.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { EnvConfigService } from '../core/env-config/env-config.service';
import { StudyManagerAuthService } from '../core/services/study-manager-auth/study-manager-auth.service';
import { UserStatusEnum } from '../user/user-status.enum';

export class Auth0User extends User {
  user_metadata: {
    uid: number;
    status: UserStatusEnum;
    language: string;
  } | undefined;
}

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth0 = inject(Auth0);
  const store = inject(Store);
  const router = inject(Router);
  const envService = inject(EnvConfigService);
  const permissionsService = inject(PermissionService);
  const ngxPermissionsService = inject(NgxPermissionsService);
  const studyManagerAuthService = inject(StudyManagerAuthService);

  const query = route.queryParams;
  // Handle errors from Auth0
  if (query.error) {
    return router.navigate(['/not-found']);
  }


  return auth0.isAuthenticated$.pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        return auth0.loginWithRedirect({
          appState: { target: state.url },
          authorizationParams: {
            audience: envService.config().auth0.audience,
            redirect_uri: `${window.location.origin}/auth-callback`,
          },
        });
      }

      return auth0.user$.pipe(
        map((user) => {
          const meta = (user as Auth0User)?.user_metadata;
          if (meta?.status !== UserStatusEnum.Active && meta?.status !== UserStatusEnum.PasswordExpired) {
            router.navigate(['/forbidden']);
            return null;
          }
          return user;
        }),
        filter((user): user is User => !!user),
        take(1),
        switchMap((user) => auth0.getAccessTokenSilently({ detailedResponse: true })
          .pipe(
            switchMap((token) => {
              // The platform token is obtained first: the password-expired screen needs it to set the new password
              const authorized$ = studyManagerAuthService.isAuthenticated()
                ? of(null)
                : studyManagerAuthService.authorize(token.access_token);

              return authorized$.pipe(
                map((response) => {
                  const isPasswordExpired =
                    (user as Auth0User)?.user_metadata?.status === UserStatusEnum.PasswordExpired ||
                    response?.user?.status === UserStatusEnum.PasswordExpired;

                  return isPasswordExpired ? router.createUrlTree(['/password-expired']) : user;
                }),
              );
            }),
            catchError((error) => {
              if (error?.error === 'missing_refresh_token' || error?.error === 'login_required') {
                auth0.loginWithRedirect({
                  appState: { target: state.url },
                  authorizationParams: {
                    audience: envService.config().auth0.audience,
                    redirect_uri: `${window.location.origin}/auth-callback`,
                  },
                });
                return EMPTY;
              }
              return EMPTY;
            }),
          )),
        tap((user: User | null) => {
          if (!(user instanceof UrlTree)) {
            store.dispatch(getUserBySub({ user }));
            store.dispatch(getUsersByOrganization());
            store.dispatch(fetchCurrentSponsor());
          }
        }),
      );
    }),
    switchMap((result) => {
      if (result instanceof UrlTree) {
        return of(result);
      }

      return permissionsService.getUserPermissions().pipe(
        catchError(() => {
          ngxPermissionsService.flushPermissions();
          return EMPTY;
        }),
        tap((permissions) => {
          ngxPermissionsService.loadPermissions(permissions);
        }),
        map(() => true),
      );
    }),
  );
};
